import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useBranding, useUpsertBranding } from "@/hooks/useBranding";
import { TYPOGRAPHY_PRESETS, type FontPairing, type TypographyPreset } from "@/lib/branding-keys";

function useGoogleFontPreview(families: string[]) {
  // Inject a <link> that preloads the current preview fonts so the cards show
  // the real typefaces. Separate from the runtime font system.
  useEffect(() => {
    const unique = Array.from(new Set(families.filter(Boolean)));
    if (unique.length === 0) return;
    const href =
      "https://fonts.googleapis.com/css2?" +
      unique
        .map((f) => `family=${encodeURIComponent(f)}:wght@400;600;700&display=swap`)
        .join("&");
    const existing = document.querySelector<HTMLLinkElement>(`link[data-branding-preview]`);
    if (existing) existing.href = href;
    else {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.setAttribute("data-branding-preview", "true");
      document.head.appendChild(link);
    }
  }, [families]);
}

function matchesPreset(heading: FontPairing | null, body: FontPairing | null, p: TypographyPreset) {
  return heading?.family === p.heading.family && body?.family === p.body.family;
}

export function TypographyTab() {
  const { getFont } = useBranding();
  const upsert = useUpsertBranding();

  const serverHeading = getFont("typography.heading");
  const serverBody = getFont("typography.body");

  const activePresetId = useMemo(() => {
    return TYPOGRAPHY_PRESETS.find((p) => matchesPreset(serverHeading, serverBody, p))?.id ?? null;
  }, [serverHeading, serverBody]);

  const [selectedId, setSelectedId] = useState<string | null>(activePresetId);

  useEffect(() => {
    setSelectedId(activePresetId);
  }, [activePresetId]);

  const families = TYPOGRAPHY_PRESETS.flatMap((p) => [p.heading.family, p.body.family]);
  useGoogleFontPreview(families);

  const dirty = selectedId !== activePresetId;

  const save = () => {
    if (!selectedId) return;
    const preset = TYPOGRAPHY_PRESETS.find((p) => p.id === selectedId);
    if (!preset) return;
    upsert.mutate(
      [
        { key: "typography.heading", value_json: preset.heading },
        { key: "typography.body", value_json: preset.body },
      ],
      {
        onSuccess: () => toast.success(`Applied: ${preset.label}`),
        onError: (e) => toast.error((e as Error).message || "Save failed"),
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>Font pairing</CardTitle>
            <CardDescription>
              Pick a heading + body pairing. Applies site-wide. More pairings
              can be added in a future release.
            </CardDescription>
          </div>
          {dirty && (
            <Button size="sm" onClick={save} disabled={upsert.isPending}>
              {upsert.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Apply pairing
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2">
        {TYPOGRAPHY_PRESETS.map((p) => {
          const selected = selectedId === p.id;
          const active = activePresetId === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedId(p.id)}
              className={`group relative text-left rounded-lg border p-4 transition-colors ${
                selected
                  ? "border-primary ring-2 ring-primary/30 bg-muted/40"
                  : "border-border/60 hover:border-primary/40 hover:bg-muted/30"
              }`}
            >
              {active && (
                <Badge variant="secondary" className="absolute top-2 right-2 gap-1 text-[10px]">
                  <Check className="h-3 w-3" /> Current
                </Badge>
              )}
              <div
                className="text-2xl leading-tight mb-1"
                style={{ fontFamily: `"${p.heading.family}", serif` }}
              >
                Tristano's Catering
              </div>
              <div
                className="text-sm text-muted-foreground leading-snug mb-3"
                style={{ fontFamily: `"${p.body.family}", system-ui, sans-serif` }}
              >
                Thoughtfully prepared for weddings, corporate events, and
                private gatherings.
              </div>
              <div className="flex items-center justify-between gap-2 pt-2 border-t border-border/60">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{p.label}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{p.description}</p>
                </div>
                <span className="text-[10px] text-muted-foreground shrink-0 font-mono">
                  {p.heading.family.split(" ")[0]} / {p.body.family.split(" ")[0]}
                </span>
              </div>
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
}
